<script setup>
  import {inject, ref, watch} from "vue";

  import ComponentUnsavedChanges from "@components/Alerts/UnsavedChanges.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsPatientAttributesPatientAddressAttribute",
  });

  const props = defineProps({
    attribute: {
      type: Object,
      default: () => ({}),
    },
    patient: {
      type: Object,
      default: () => ({}),
    },
    recentlyAdded: {
      type: Boolean,
      default: false,
    },
  });

  const broadcast = inject("$broadcast");

  const refConfirmationDialog = ref(null);

  const activeEdit = ref(props.recentlyAdded);
  const form = useForm({
    type: props.attribute.type,
    payload: {
      supplement: props.attribute?.payload?.supplement,
      street: props.attribute?.payload?.street,
      zip: props.attribute?.payload?.zip,
      city: props.attribute?.payload?.city,
      email: props.attribute?.payload?.email,
      telephone: props.attribute?.payload?.telephone,
    },
  });

  watch(
    () => props.recentlyAdded,
    (newValue) => {
      activeEdit.value = newValue;
    },
  );

  function cancelEdit() {
    // was recently added AND was NOT saved once
    if (props.recentlyAdded && !form.wasSuccessful) {
      remove();
    } else {
      form.reset();
      activeEdit.value = false;
    }
  }

  function save() {
    form.put(route("attributes.update", {patient: props.patient.id, attribute: props.attribute.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "patient"],
      onSuccess: () => {
        activeEdit.value = false;
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }

  function confirmRemove() {
    refConfirmationDialog.value.open();
  }

  function remove() {
    form.delete(route("attributes.destroy", {patient: props.patient.id, attribute: props.attribute.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "patient"],
      onSuccess: () => {
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }
</script>

<template>
  <form :data-test="getTestId('patient-address-attribute-form')" @submit.prevent="save">
    <ComponentCard class="group relative hover:bg-gray-100" :test-id="getTestId('patient-address-attribute-form-card')">
      <div class="flex items-baseline justify-between">
        <h6 class="text-xl">Anschrift des Patienten</h6>

        <MedSpinner v-if="form.processing" size="xs" :data-test="getTestId('patient-address-attribute-spinner')" />

        <ComponentUnsavedChanges v-else-if="form.isDirty || form.recentlySuccessful" :form="form" />

        <span v-else class="text-xs text-gray-500">Aktualisiert {{ attribute.updated_at }}</span>
      </div>

      <template v-if="activeEdit">
        <p v-if="$page.props.client.license.is_pro" class="text-sm text-gray-600">
          Die Anschrift wird bei geteilten Patienten nicht übermittelt.
        </p>

        <div class="mt-4 grid grid-cols-4 gap-8">
          <ComponentInput
            v-model="form.payload.supplement"
            :encrypted="true"
            class="col-span-4 md:col-span-2"
            label="Adresszusatz"
            helper-text="Bspw. c/o, wenn Patient bei dritter Person wohnhaft ist"
            :validation="form.errors['payload.supplement']"
            :test-id="getTestId('patient-address-attribute-form-card-supplement')"
          />

          <ComponentInput
            v-model="form.payload.street"
            :encrypted="true"
            class="col-span-4 col-start-1 md:col-span-3"
            label="Straße, Hausnr."
            :validation="form.errors['payload.street']"
            :test-id="getTestId('patient-address-attribute-form-card-street')"
          />

          <ComponentInput
            v-model="form.payload.zip"
            :encrypted="true"
            class="col-span-1 col-start-1"
            label="PLZ"
            :validation="form.errors['payload.zip']"
            :test-id="getTestId('patient-address-attribute-form-card-zip')"
          />

          <ComponentInput
            v-model="form.payload.city"
            :encrypted="true"
            class="col-span-3 md:col-span-2"
            label="Ort"
            :validation="form.errors['payload.city']"
            :test-id="getTestId('patient-address-attribute-form-card-city')"
          />

          <ComponentInput
            v-model="form.payload.email"
            :encrypted="true"
            class="col-span-4 col-start-1 md:col-span-2"
            label="E-Mail"
            :validation="form.errors['payload.email']"
            :test-id="getTestId('patient-address-attribute-form-card-email')"
          />

          <ComponentInput
            v-model="form.payload.telephone"
            :encrypted="true"
            class="col-span-4 col-start-1 md:col-span-2"
            label="Telefon"
            :validation="form.errors['payload.telephone']"
            :test-id="getTestId('patient-address-attribute-form-card-telephone')"
          />
        </div>
      </template>

      <template v-else>
        <div class="mt-0.5 min-h-4">
          <template v-if="patient.is_fake">
            <p class="text-sm text-gray-600">Die Anschrift wird bei geteilten Patienten nicht übermittelt.</p>
          </template>

          <div :data-encrypted="attribute.payload?.supplement" />
          <div :data-encrypted="attribute.payload?.street" />

          <div>
            <span :data-encrypted="attribute.payload?.zip" />
            <span :data-encrypted="attribute.payload?.city" />
          </div>

          <div>
            <div v-if="attribute.payload?.email">
              E-Mail:
              <span :data-encrypted="attribute.payload?.email" />
            </div>

            <div v-if="attribute.payload?.telephone">
              Tel:
              <span :data-encrypted="attribute.payload?.telephone" />
            </div>
          </div>
        </div>

        <div class="absolute right-2 bottom-2 flex gap-2">
          <MedButton
            aria-label="Anschrift des Patienten bearbeiten"
            flat
            variant="primary"
            icon="edit"
            rounded="full"
            :data-test="getTestId('patient-address-attribute-form-card-edit-button')"
            @click="activeEdit = true"
          />

          <MedButton
            aria-label="Anschrift des Patienten löschen"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('patient-address-attribute-form-card-delete-button')"
            @click="confirmRemove()"
          />
        </div>
      </template>

      <template v-if="activeEdit" #actions>
        <MedButton
          type="submit"
          variant="primary"
          :disabled="form.processing"
          :data-test="getTestId('patient-address-attribute-form-card-save-button')"
        >
          Speichern
        </MedButton>

        <MedButton
          :disabled="form.processing"
          :data-test="getTestId('patient-address-attribute-form-card-cancel-edit-button')"
          @click="cancelEdit()"
        >
          Abbrechen
        </MedButton>
      </template>
    </ComponentCard>
  </form>

  <ComponentConfirmationDialog
    ref="refConfirmationDialog"
    title="Sind Sie sicher?"
    content="Die Angabe „Patientenanschrift“ wird gelöscht."
    @confirmed="remove()"
  />
</template>
